import { AccessType } from "@/lib/users/types";
import { SupabaseClient } from "@supabase/supabase-js";

/**
 * Database operations for user_event_access table
 */

export interface UserEventAccess {
  user_id: number;
  event_id: number | null;
  access_type: AccessType;
  granted_by: number;
  created_at?: string;
}

export interface UserEventAccessFilter {
  userId?: number;
  eventId?: number | null;
  accessType?: AccessType;
  grantedBy?: number;
}

/**
 * Get user event access records
 */
export async function getUserEventAccess(supabase: SupabaseClient, filter: UserEventAccessFilter) {
  let query = supabase.schema("nowadays").from("user_event_access").select("*");

  if (filter.userId !== undefined) {
    query = query.eq("user_id", filter.userId);
  }
  if (filter.eventId !== undefined) {
    if (filter.eventId === null) {
      query = query.is("event_id", null);
    } else {
      query = query.eq("event_id", filter.eventId);
    }
  }
  if (filter.accessType !== undefined) {
    query = query.eq("access_type", filter.accessType);
  }
  if (filter.grantedBy !== undefined) {
    query = query.eq("granted_by", filter.grantedBy);
  }

  return await query;
}

/**
 * Get user's event access for a specific event
 */
export async function getUserEventAccessByEvent(supabase: SupabaseClient, userId: number, eventId: number) {
  return await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("*")
    .eq("user_id", userId)
    .eq("event_id", eventId)
    .single();
}

/**
 * Get all events a user has access to
 */
export async function getUserAccessibleEvents(supabase: SupabaseClient, userId: number) {
  return await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("event_id, access_type")
    .eq("user_id", userId);
}

/**
 * Get all users who have access to an event
 */
export async function getEventUsers(supabase: SupabaseClient, eventId: number, accessType?: AccessType) {
  let query = supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("user_id, access_type, granted_by")
    .eq("event_id", eventId);

  if (accessType) {
    query = query.eq("access_type", accessType);
  }

  return await query;
}

/**
 * Create or update user event access
 */
export async function upsertUserEventAccess(supabase: SupabaseClient, access: UserEventAccess) {
  // Check if access already exists
  const { data: existing } = await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("*")
    .eq("user_id", access.user_id)
    .eq("event_id", access.event_id || null)
    .single();

  if (existing) {
    // Update existing access
    return await supabase
      .schema("nowadays")
      .from("user_event_access")
      .update({
        access_type: access.access_type,
        granted_by: access.granted_by,
      })
      .eq("user_id", access.user_id)
      .eq("event_id", access.event_id || null);
  } else {
    // Insert new access
    return await supabase.schema("nowadays").from("user_event_access").insert(access);
  }
}

/**
 * Remove user event access
 */
export async function removeUserEventAccess(supabase: SupabaseClient, userId: number, eventId: number) {
  return await supabase
    .schema("nowadays")
    .from("user_event_access")
    .delete()
    .eq("user_id", userId)
    .eq("event_id", eventId);
}

/**
 * Remove all event access for a user
 */
export async function removeAllUserEventAccess(supabase: SupabaseClient, userId: number) {
  return await supabase.schema("nowadays").from("user_event_access").delete().eq("user_id", userId);
}

/**
 * Check if user has access to event
 */
export async function hasEventAccess(
  supabase: SupabaseClient,
  userId: number,
  eventId: number,
  requiredAccessType?: AccessType
): Promise<boolean> {
  const { data, error } = await getUserEventAccessByEvent(supabase, userId, eventId);

  if (error || !data) {
    return false;
  }

  if (requiredAccessType) {
    // Check if user has the required access level or higher
    const accessHierarchy = [AccessType.ATTENDEE, AccessType.ADMIN, AccessType.OWNER];
    const userLevel = accessHierarchy.indexOf(data.access_type);
    const requiredLevel = accessHierarchy.indexOf(requiredAccessType);
    return userLevel >= requiredLevel;
  }

  return true;
}

/**
 * Get event owners
 */
export async function getEventOwners(supabase: SupabaseClient, eventId: number) {
  return await getEventUsers(supabase, eventId, AccessType.OWNER);
}

/**
 * Get event admins
 */
export async function getEventAdmins(supabase: SupabaseClient, eventId: number) {
  return await getEventUsers(supabase, eventId, AccessType.ADMIN);
}

/**
 * Get event attendees
 */
export async function getEventAttendees(supabase: SupabaseClient, eventId: number) {
  return await getEventUsers(supabase, eventId, AccessType.ATTENDEE);
}

/**
 * Demote current owners to admin when setting a new owner
 */
export async function demoteCurrentOwners(supabase: SupabaseClient, eventId: number, excludeUserId?: number) {
  let query = supabase
    .schema("nowadays")
    .from("user_event_access")
    .update({ access_type: AccessType.ADMIN })
    .eq("event_id", eventId)
    .eq("access_type", AccessType.OWNER);

  if (excludeUserId) {
    query = query.neq("user_id", excludeUserId);
  }

  return await query;
}
